import React, { FC, useCallback, useMemo } from "react";

import { Modal, Stack } from "react-bootstrap";
import { ModalProps } from "react-bootstrap";

import currency from "currency.js";
import { ReactSVG } from "react-svg";

import {
  ParticipantStatus,
  PaymentLineDetailsWithResaleDetails,
  RemittanceWithPaymentLines,
  SortDirection,
  useGetPresignedDownloadUrlMutation,
  useListGroupedPaymentLinesQuery,
} from "@api/graphql";
import { DownloadIcon } from "@assets/icons";
import { useIsAuthUserAllowed, useIsAuthorisedUser } from "@components/permission_control/ProtectedComponent";
import { GroupedRoyalties } from "@components/royalties/GroupedRoyalties";
import { InternalRoles } from "@roles";
import { FilterByTyped, SortByTyped, documentKeyFromS3Location, formatISODateToFormat } from "@util/index";

import styles from "./PastPayments.module.scss";

const FileLink: FC<{ fileId: number; fileType: string }> = ({ fileId, fileType }) => (
  <span className={"b3 bold"}>
    Remittance-{fileId}.{fileType}
  </span>
);

interface DownloadModalProps extends ModalProps {
  remittance_id: RemittanceWithPaymentLines["id"];
  csv_s3_location: RemittanceWithPaymentLines["csv_s3_location"];
  pdf_s3_location: RemittanceWithPaymentLines["pdf_s3_location"];
}

const DownloadModal: FC<DownloadModalProps> = ({ remittance_id, csv_s3_location, pdf_s3_location, ...rest }) => {
  const [getPresignedDownloadUrl] = useGetPresignedDownloadUrlMutation();

  const getUrl = useCallback(
    async (key: string) => {
      try {
        const { data } = await getPresignedDownloadUrl({
          variables: { documentKey: key },
        });
        const downloadUrl = data?.getPresignedDownloadURL?.downloadUrl;
        if (!downloadUrl) {
          return;
        }
        window.open(downloadUrl, "_blank");
      } catch (error) {
        return;
      }
    },
    [getPresignedDownloadUrl],
  );

  const handleDownloadCSV = useCallback(() => {
    if (!csv_s3_location) {
      return;
    }
    const fileKey = documentKeyFromS3Location(csv_s3_location);
    getUrl(fileKey);
  }, [csv_s3_location, getUrl]);

  const handleDownloadPDF = useCallback(() => {
    if (!pdf_s3_location) {
      return;
    }
    const fileKey = documentKeyFromS3Location(pdf_s3_location);
    getUrl(fileKey);
  }, [getUrl, pdf_s3_location]);

  return (
    <Modal {...rest} size="lg" centered className="b3">
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">Remittance Documents</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Stack direction="vertical" gap={3}>
          {remittance_id && (
            <div onClick={handleDownloadCSV} className={styles["download-link"]}>
              <FileLink fileId={remittance_id} fileType="csv" />
            </div>
          )}
          {remittance_id && (
            <div onClick={handleDownloadPDF} className={styles["download-link"]}>
              <FileLink fileId={remittance_id} fileType="pdf" />
            </div>
          )}
        </Stack>
      </Modal.Body>
    </Modal>
  );
};

export const PastPaymentAccordionHeader: FC<{ remittance: RemittanceWithPaymentLines }> = ({ remittance }) => {
  const [showDownload, setShowDownload] = React.useState(false);
  const handleDownloadMenuClicked = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowDownload(true);
  };
  const handleOnDownloadModalClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };
  const paymentTotal: number = useMemo(() => {
    if (!remittance.payment_lines) {
      return 0;
    }
    return remittance.payment_lines?.reduce((acc, paymentLine) => {
      if (!paymentLine) {
        return acc;
      } else {
        const currentRoyaltyNet = paymentLine.royalty_net || 0;
        return acc + currentRoyaltyNet;
      }
    }, 0);
  }, [remittance.payment_lines]);

  return (
    <>
      <Stack direction="horizontal" className="w-100 b3 bold">
        <span className="col-md-4">
          {remittance.date_created &&
            formatISODateToFormat({ isoDate: remittance.date_created, targetDateFormat: "MMMM YYYY" })}
        </span>

        <div className="col-md-4 d-flex">
          <span className="col-md-6 d-flex justify-content-end">Payment Total:</span>
          <span className="col-md-6 ps-2"> {currency(paymentTotal).format()}</span>
        </div>

        <div className="ms-auto px-3" onClick={handleDownloadMenuClicked}>
          {remittance.pdf_document_id && remittance.csv_document_id && (
            <Stack direction="horizontal" gap={2} className={styles["download-link"]}>
              <ReactSVG src={DownloadIcon} />
              <span className={"b3 bold"}>Downloads</span>
            </Stack>
          )}
        </div>

        <div onClick={handleOnDownloadModalClick}>
          <DownloadModal
            show={showDownload}
            onHide={() => setShowDownload(false)}
            remittance_id={remittance.id}
            csv_s3_location={remittance.csv_s3_location}
            pdf_s3_location={remittance.pdf_s3_location}
          />
        </div>
      </Stack>
    </>
  );
};

export const PastPayments: FC<{ displayBanner?: boolean; payeeId?: number }> = ({ displayBanner, payeeId }) => {
  const isInternalUser = useIsAuthUserAllowed(InternalRoles);
  const sort_by: SortByTyped<RemittanceWithPaymentLines> = {
    field: "date_created",
    direction: SortDirection.DESCENDING,
  };
  const filter_by: FilterByTyped<PaymentLineDetailsWithResaleDetails>[] = [];
  // payee_id filter is only allowed for VA/MS/Data; royalty recipients are scoped by the API to their own participant
  if (isInternalUser && payeeId) {
    filter_by.push({ field: "payee_id", eq: payeeId.toString() });
  }
  const isPendingParticipant = useIsAuthorisedUser({
    requiredParticipantStatus: ParticipantStatus.PENDING_APPROVAL,
  });
  const getAccordionHeader = (remittance: RemittanceWithPaymentLines) => (
    <PastPaymentAccordionHeader remittance={remittance} />
  );

  return (
    <GroupedRoyalties
      queryFunction={useListGroupedPaymentLinesQuery}
      getQueryVariables={(page) => ({
        // if current user attached to a participant is pending approval, then don't show any data
        input: { page, limit: isPendingParticipant ? 0 : 10, filter_by, sort_by },
      })}
      updateQueryVariablesWithFilter={({ input: { page, limit, filter_by, sort_by } }, new_filter_by) => ({
        input: {
          page,
          limit,
          filter_by: filter_by.concat(new_filter_by ? new_filter_by : []),
          sort_by,
        },
      })}
      getRemittancesFromQueryData={(data) => data.listGroupedPaymentLines.results}
      getPaginatedOutputFromQueryData={(data) => data.listGroupedPaymentLines}
      getAccordionHeader={getAccordionHeader}
      sourcePage={"PastPayments"}
      customTableStyleClassName={styles["customized-column-width"]}
      displayBanner={displayBanner}
    />
  );
};
